import * as React from 'react';

import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/icon';
import {
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  type EntitySelection,
  type EntitySelectorConfig,
} from '../entity-selector';

import { useDataTableAddColumn } from './data-table-context';

// ---------------------------------------------------------------------------
// DataTableAddColumnHeader — the actions column's "+" header
// ---------------------------------------------------------------------------
//
// A searchable picker of the columns currently hidden; picking one shows it.
// An entity selector for the same reason the column-settings panel is one:
// the hidden set is unbounded (every workspace custom field is a column), so
// a flat menu stops being navigable long before the real roster does.
//
// Selection state is deliberately EMPTY: this is an add gesture, not a
// roster. A picked column becomes visible, leaves the hidden pool, and the
// popup stays open for the next add.

const POOL_ID = 'hidden-columns';
const EMPTY_SELECTIONS: EntitySelection[] = [];

export function DataTableAddColumnHeader(): React.ReactElement | null {
  const addColumn = useDataTableAddColumn();

  const config = React.useMemo<EntitySelectorConfig>(
    () => ({
      pools: [
        {
          id: POOL_ID,
          label: 'Add column',
          items: (addColumn?.entries ?? []).map((entry) => ({
            id: entry.id,
            title: entry.label,
          })),
          hideAvatars: true,
        },
      ],
      searchPlaceholder: 'Search columns...',
      emptyMessage: 'No columns found',
      noItemsMessage: 'All columns are shown',
    }),
    [addColumn?.entries],
  );

  const handleSelectionsChange = React.useCallback(
    (next: EntitySelection[]) => {
      // Value is pinned empty, so `next` holds exactly the item just picked.
      for (const selection of next) addColumn?.onAdd(selection.id);
    },
    [addColumn],
  );

  // No handler wired, or nothing hidden — no affordance to offer.
  if (!addColumn || addColumn.entries.length === 0) return null;

  return (
    <EntitySelector
      config={config}
      value={EMPTY_SELECTIONS}
      onValueChange={handleSelectionsChange}
    >
      <EntitySelectorTrigger
        render={
          <Button variant="ghost" size="icon-xs" aria-label="Add column">
            <Icon name="plus" />
          </Button>
        }
      />
      <EntitySelectorContent align="end" size="sm" maxHeight={400}>
        <EntitySelectorInput placeholder="Search columns..." />
        <EntitySelectorPool value={POOL_ID} hideSelectionHighlight />
      </EntitySelectorContent>
    </EntitySelector>
  );
}
